import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CtaButton, SelectInput, TextInput } from '@tassomutuo-backoffice/ui';
import { toast } from 'react-toastify';

import {
  ManagedLeadAdvancementField,
  ManagedLeadCategory,
  ManagedLeadDetail,
  ManagedLeadDropRequestPayload,
  ManagedLeadProductPath,
  ManagedLeadTransition,
  ManagedLeadUpdatePayload,
  requestManagedLeadDrop,
  updateManagedLead,
} from '../../../services/api/managed-leads/managedLeads';
import {
  describeManagedLeadDropFailure,
  describeManagedLeadPendingDrop,
  MANAGED_LEAD_DROP_SENT_MESSAGE,
} from '../managedLeadDropFeedback';
import {
  managedLeadDetailQueryKey,
  managedLeadListQueryKey,
  managedLeadNotesQueryKey,
} from '../managedLeadQueryKeys';
import {
  advancementFieldLabels,
  ADVANCEMENT_UNSUPPORTED_MESSAGE,
  advancementPayload,
  hasUnsupportedAdvancementField,
  initialAdvancementValues,
  ManagedLeadAdvancementErrors,
  ManagedLeadAdvancementValues,
  validateAdvancementValues,
} from '../managedLeadAdvancementData';
import { createManagedLeadIdempotencyKey } from '../managedLeadIdempotency';
import { canUpdateManagedLead } from '../managedLeadUpdateAvailability';
import ManagedLeadAdvancementFields from './ManagedLeadAdvancementFields';
import ManagedLeadFailureAlert from './ManagedLeadFailureAlert';
import {
  describeManagedLeadUpdateFailure,
  describeManagedLeadUpdateOutcome,
} from '../managedLeadUpdateFeedback';

/** Contract limit: the backend validates the trimmed Nota at 5.000 characters. */
const NOTE_MAX_LENGTH = 5000;

const STAGE_REQUIRED_MESSAGE = 'Seleziona';
const NOTE_REQUIRED_MESSAGE =
  'Per favore inserisci le note. Per noi sono molto importanti per capire lo stato di avanzamento';
const NOTE_TOO_LONG_MESSAGE = `La nota non può superare ${NOTE_MAX_LENGTH} caratteri.`;
const REASON_REQUIRED_MESSAGE = 'Seleziona una motivazione';

/**
 * The Drop entry of the stage menu. It is not a stage id — asking for a Drop is
 * not an advancement — so it can never collide with one the server sends.
 */
const DROP_TARGET_VALUE = 'drop';

/**
 * One submission: the payload, the key minted for it and the destination it was
 * aimed at. A retry re-sends this attempt verbatim instead of rebuilding it —
 * after a partial success the stage has already moved, so a payload derived
 * from the re-read detail would no longer be the request the server is holding
 * open under that key.
 */
interface UpdateAttempt {
  payload: ManagedLeadUpdatePayload;
  key: string;
  target: ManagedLeadTransition;
  from: ManagedLeadCategory;
}

interface ManagedLeadUpdateSectionProps {
  open: boolean;
  product: ManagedLeadProductPath;
  dealId: string;
  detail: ManagedLeadDetail;
  onComplete: () => void;
  onRequestClose: () => void;
  onInteractionStateChange: (state: {
    dirty: boolean;
    pending: boolean;
  }) => void;
}

const REFRESH_FAILED_MESSAGE =
  'Aggiornamento salvato, ma non è stato possibile ricaricare i dati. Ricarica la pagina per vedere le informazioni aggiornate.';

/**
 * "Fornisci aggiornamento" for every managed lead product: the operator keeps
 * the current stage with a Nota, or advances to one of the transitions the
 * backend offers. Every rule — which stages are reachable, which of them want a
 * Nota, whether Drop is available — comes from the detail contract, so the
 * backoffice holds no lifecycle matrix of its own.
 */
const ManagedLeadUpdateSection = ({
  open,
  product,
  dealId,
  detail,
  onComplete,
  onRequestClose,
  onInteractionStateChange,
}: ManagedLeadUpdateSectionProps) => {
  const queryClient = useQueryClient();
  const sectionRef = useRef<HTMLElement>(null);
  const [note, setNote] = useState('');
  const [chosenStage, setChosenStage] = useState<string | null>(null);
  const [dropReason, setDropReason] = useState('');
  const [outstandingOutcome, setOutstandingOutcome] = useState<{
    message: string;
    retryable: boolean;
  } | null>(null);
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null
  );
  const [advancementValues, setAdvancementValues] =
    useState<ManagedLeadAdvancementValues>({});
  const [fieldErrors, setFieldErrors] = useState<ManagedLeadAdvancementErrors>(
    {}
  );
  /**
   * The form the values on screen were initialized for: the selection they
   * belong to, and the persisted data they were read from. When either moves -
   * the operator picks another stage, or a refetch brings new HubSpot values -
   * the fields start again from the backend, and the edits made against the old
   * form are gone rather than carried into the new one.
   */
  const [initializedFor, setInitializedFor] = useState<string | null>(null);
  const attemptRef = useRef<UpdateAttempt | null>(null);
  /**
   * The Drop request on screen. It carries no idempotency key — the server
   * deduplicates a Drop by product and Deal — so Riprova only has to re-send
   * the reason and the Nota the operator already wrote.
   */
  const dropAttemptRef = useRef<ManagedLeadDropRequestPayload | null>(null);
  const inFlightRef = useRef(false);

  /**
   * Puts the form back on one selection with nothing typed against it. The
   * Advancement data itself is not set here: it is read from the detail while
   * rendering, so a reset always lands on what the backend holds rather than on
   * what the operator had entered.
   */
  const resetForm = (stage: string | null) => {
    setChosenStage(stage);
    setNote('');
    setDropReason('');
    setFieldErrors({});
    setInitializedFor(null);
  };

  const mutation = useMutation({
    mutationFn: (attempt: UpdateAttempt) =>
      updateManagedLead(product, dealId, attempt.payload, attempt.key),
    onSuccess: (result, attempt) => {
      // Anything the Deal write changed is applied and irreversible even when
      // the Nota failed: the detail and both product lists are stale either
      // way, and a lead that reached a terminal stage has to show up in its new
      // category. A Nota is commercial activity, so the cards age with it too.
      const refreshes: Promise<void>[] = [];
      if (result.stageUpdated || result.fieldsUpdated || result.note.created) {
        refreshes.push(
          queryClient.invalidateQueries(
            { queryKey: managedLeadDetailQueryKey(product, dealId) },
            { throwOnError: true }
          ),
          queryClient.invalidateQueries(
            { queryKey: managedLeadListQueryKey(product) },
            { throwOnError: true }
          )
        );
      }
      if (result.note.created) {
        refreshes.push(
          queryClient.invalidateQueries(
            { queryKey: managedLeadNotesQueryKey(product, dealId) },
            { throwOnError: true }
          )
        );
      }
      void Promise.all(refreshes).catch(() =>
        toast.warning(REFRESH_FAILED_MESSAGE)
      );

      // A partial success keeps text, key and payload: only the Nota is left.
      if (result.outcome === 'partial_success') {
        const outcome = describeManagedLeadUpdateOutcome(
          result,
          attempt.target,
          attempt.from
        );
        setOutstandingOutcome({
          message: outcome.message,
          retryable: outcome.retryable,
        });
        return;
      }

      attemptRef.current = null;
      setOutstandingOutcome(null);
      toast.success(
        describeManagedLeadUpdateOutcome(result, attempt.target, attempt.from)
          .message
      );
      resetForm(null);
      onComplete();
    },
    onError: (error) => {
      const failure = describeManagedLeadUpdateFailure(error);
      // The key is burnt, but the attempt still holds the payload a retry has
      // to carry, so it is re-keyed instead of thrown away.
      if (failure.renewsIdempotencyKey && attemptRef.current) {
        attemptRef.current = {
          ...attemptRef.current,
          key: createManagedLeadIdempotencyKey(),
        };
      }
      if (failure.refetchesDetail) {
        queryClient.invalidateQueries({
          queryKey: managedLeadDetailQueryKey(product, dealId),
        });
      }
    },
    onSettled: () => {
      inFlightRef.current = false;
    },
  });

  const dropMutation = useMutation({
    mutationFn: (payload: ManagedLeadDropRequestPayload) =>
      requestManagedLeadDrop(product, dealId, payload),
    onSuccess: (pendingDrop) => {
      // Lock the detail immediately. The following refetch reconciles all
      // fields with the server, but the consultant must not see an update CTA
      // between the accepted request and that response.
      queryClient.setQueryData<ManagedLeadDetail>(
        managedLeadDetailQueryKey(product, dealId),
        (current: ManagedLeadDetail | undefined) =>
          current ? { ...current, pendingDrop } : current
      );
      // The lead does not move: what changed is the pending badge the detail
      // and the lists carry, plus the Nota the request wrote on the Deal.
      void Promise.all([
        queryClient.invalidateQueries(
          { queryKey: managedLeadDetailQueryKey(product, dealId) },
          { throwOnError: true }
        ),
        queryClient.invalidateQueries(
          { queryKey: managedLeadListQueryKey(product) },
          { throwOnError: true }
        ),
        queryClient.invalidateQueries(
          { queryKey: managedLeadNotesQueryKey(product, dealId) },
          { throwOnError: true }
        ),
      ]).catch(() => toast.warning(REFRESH_FAILED_MESSAGE));

      dropAttemptRef.current = null;
      resetForm(null);
      toast.success(MANAGED_LEAD_DROP_SENT_MESSAGE);
      onComplete();
    },
    onError: (error) => {
      const failure = describeManagedLeadDropFailure(error);
      if (failure.pendingDrop) {
        queryClient.setQueryData<ManagedLeadDetail>(
          managedLeadDetailQueryKey(product, dealId),
          (current: ManagedLeadDetail | undefined) =>
            current ? { ...current, pendingDrop: failure.pendingDrop } : current
        );
      }
      if (failure.refetchesDetail) {
        queryClient.invalidateQueries({
          queryKey: managedLeadDetailQueryKey(product, dealId),
        });
      }
    },
    onSettled: () => {
      inFlightRef.current = false;
    },
  });

  const { transitions } = detail;
  // Drop availability comes from the current stage's requirements and never
  // from `updatable`: the read-only in-progress stages accept no advancement
  // and still allow the request, so the form has to be on screen for them too.
  const dropAllowed = detail.requirements.dropAllowed;
  const dropFailure = dropMutation.isError
    ? describeManagedLeadDropFailure(dropMutation.error)
    : null;
  // The re-read detail is the standing source; a server that has just refused a
  // duplicate names the pending request itself, so the badge does not wait on
  // that refetch.
  const pendingDrop =
    detail.pendingDrop ??
    (dropMutation.isSuccess ? dropMutation.data : null) ??
    dropFailure?.pendingDrop ??
    null;
  const readOnly = Boolean(pendingDrop);
  const canUpdate = canUpdateManagedLead(detail, pendingDrop);
  const dropRequestable = dropAllowed && !pendingDrop;
  // Nothing else can be done from here: there is no menu to open, so the form
  // is the Drop request itself. Choosing it still takes a reason and a Nota —
  // what is skipped is a choice with a single possible answer.
  const dropOnly = !canUpdate && dropRequestable;
  const dropChosen = chosenStage === DROP_TARGET_VALUE;
  const dropSelected = dropRequestable && (dropOnly || dropChosen);
  // The default is the current stage — the update that changes nothing — and a
  // selection the contract stopped listing never survives a refetch. Where the
  // server offers no note-only update the menu opens empty on purpose: an
  // advancement must be chosen, never armed for the operator. A Drop that
  // stopped being possible falls back to nothing for the same reason: silently
  // turning it into a stage update would leave the Nota written for the Drop
  // armed behind a button that now sends something else.
  const target = dropChosen
    ? null
    : transitions.find((transition) => transition.id === chosenStage) ??
      transitions.find((transition) => transition.id === detail.stage.id) ??
      null;
  // A Drop always documents itself: the Nota is required whatever the stage the
  // request is made from.
  const noteRequired = dropSelected || target?.requirements.note === 'required';
  // Advancement data belongs to the destination, so it follows the selection
  // and never the stage the lead is on. A Drop asks for a reason instead.
  const advancementFields: ManagedLeadAdvancementField[] = dropSelected
    ? []
    : target?.requirements.fields ?? [];
  // A descriptor this release cannot render is not a field to skip: the stage
  // requires it, so the whole update is refused until the backoffice is updated.
  const unsupportedAdvancement =
    hasUnsupportedAdvancementField(advancementFields);
  const advancementLabels = advancementFieldLabels(advancementFields);

  const persistedAdvancementValues = initialAdvancementValues(
    advancementFields,
    detail.advancementData
  );
  const formKey = `${
    dropSelected ? DROP_TARGET_VALUE : target?.id ?? ''
  }|${JSON.stringify(detail.advancementData ?? {})}`;
  if (initializedFor !== formKey && !mutation.isError && !outstandingOutcome) {
    // Adjusted while rendering, not in an effect: the fields of the stage just
    // chosen must never paint with the values typed against the previous one.
    setInitializedFor(formKey);
    setAdvancementValues(persistedAdvancementValues);
    setFieldErrors({});
  }

  const sendDrop = (payload: ManagedLeadDropRequestPayload) => {
    dropAttemptRef.current = payload;
    inFlightRef.current = true;
    dropMutation.mutate(payload);
  };

  const send = (attempt: UpdateAttempt, keepOutstanding = false) => {
    attemptRef.current = attempt;
    if (!keepOutstanding) setOutstandingOutcome(null);
    inFlightRef.current = true;
    mutation.mutate(attempt);
  };

  /** The message the Nota fails on, or `null` when it is acceptable as typed. */
  const noteValidationMessage = (trimmedNote: string) => {
    if (!noteRequired) return null;
    if (!trimmedNote) return NOTE_REQUIRED_MESSAGE;
    if (trimmedNote.length > NOTE_MAX_LENGTH) return NOTE_TOO_LONG_MESSAGE;
    return null;
  };

  const submitDrop = (trimmedNote: string) => {
    if (!dropReason) {
      setValidationMessage(REASON_REQUIRED_MESSAGE);
      return;
    }
    const noteMessage = noteValidationMessage(trimmedNote);
    if (noteMessage) {
      setValidationMessage(noteMessage);
      return;
    }
    setValidationMessage(null);
    sendDrop({ reason: dropReason, note: trimmedNote });
  };

  const submit = () => {
    // A ref, not `isPending`: two clicks in the same tick both read the state
    // before React re-renders, and the second one must not reach the network.
    if (inFlightRef.current) return;

    const trimmedNote = note.trim();
    if (dropSelected) {
      submitDrop(trimmedNote);
      return;
    }

    if (!target) {
      setValidationMessage(STAGE_REQUIRED_MESSAGE);
      return;
    }

    // Nothing to send while a required datum has no input to collect it.
    if (unsupportedAdvancement) return;

    // Everything is checked at once: the operator gets one pass over the form
    // instead of one rejected field per click.
    const advancementErrors = validateAdvancementValues(
      advancementFields,
      advancementValues
    );
    const noteMessage = noteValidationMessage(trimmedNote);
    setFieldErrors(advancementErrors);
    setValidationMessage(noteMessage);
    if (noteMessage || Object.keys(advancementErrors).length) return;

    const fields = advancementPayload(advancementFields, advancementValues);
    const payload: ManagedLeadUpdatePayload = {
      expectedCurrentStage: detail.stage.id,
      targetStage: target.id,
      ...(noteRequired ? { note: trimmedNote } : {}),
      ...(fields ? { fields } : {}),
    };
    const previous = attemptRef.current;
    // The same request keeps its key so the server can replay it instead of
    // writing twice; a changed one is a new attempt and needs a new key.
    const unchanged =
      previous &&
      previous.target.id === target.id &&
      previous.payload.expectedCurrentStage === payload.expectedCurrentStage &&
      previous.payload.note === payload.note &&
      // Descriptor order is the server's, so two identical sets of values
      // always serialize the same way.
      JSON.stringify(previous.payload.fields ?? null) ===
        JSON.stringify(payload.fields ?? null);

    send(
      unchanged
        ? previous
        : {
            payload,
            key: createManagedLeadIdempotencyKey(),
            target,
            from: detail.stage.category,
          }
    );
  };

  // Riprova always re-sends the attempt as it was built. Rebuilding it from the
  // form would be wrong twice over: a partial success has already moved the
  // stage the payload was hashed with, and the form may no longer be on screen.
  const retry = () => {
    if (inFlightRef.current || !attemptRef.current) return;
    send(attemptRef.current, true);
  };

  const retryDrop = () => {
    if (inFlightRef.current || !dropAttemptRef.current) return;
    sendDrop(dropAttemptRef.current);
  };

  const failure = mutation.isError
    ? describeManagedLeadUpdateFailure(mutation.error, advancementLabels)
    : null;
  // What the operator typed comes first: a message the server wrote about the
  // previous value would otherwise sit under a field already corrected.
  const advancementErrorMessages = {
    ...(failure?.fieldMessages ?? {}),
    ...fieldErrors,
  };
  const advancementErrorSignature = Object.keys(advancementErrorMessages).join(
    '|'
  );
  const noteErrorMessage =
    validationMessage === NOTE_REQUIRED_MESSAGE ||
    validationMessage === NOTE_TOO_LONG_MESSAGE
      ? validationMessage
      : (dropSelected
          ? dropFailure?.noteFieldMessage
          : failure?.noteFieldMessage) ?? null;
  const reasonErrorMessage =
    validationMessage === REASON_REQUIRED_MESSAGE
      ? REASON_REQUIRED_MESSAGE
      : dropFailure?.reasonFieldMessage ?? null;
  const stageHelperText =
    validationMessage === STAGE_REQUIRED_MESSAGE
      ? STAGE_REQUIRED_MESSAGE
      : null;
  const pending = mutation.isPending || dropMutation.isPending;
  const blockedByStaleDetail = Boolean(
    failure?.refetchesDetail && !failure.retryable
  );
  const dirty = Boolean(
    !outstandingOutcome &&
      (chosenStage !== null ||
        note.length ||
        dropReason ||
        JSON.stringify(advancementValues) !==
          JSON.stringify(persistedAdvancementValues))
  );

  useEffect(() => {
    onInteractionStateChange({ dirty, pending });
  }, [dirty, onInteractionStateChange, pending]);

  useEffect(() => {
    if (open || outstandingOutcome) return;
    attemptRef.current = null;
    dropAttemptRef.current = null;
    setChosenStage(null);
    setNote('');
    setDropReason('');
    setValidationMessage(null);
    setFieldErrors({});
    setInitializedFor(null);
    mutation.reset();
    dropMutation.reset();
    // Mutation reset functions are stable; including the whole mutation result
    // would run this cleanup on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, outstandingOutcome]);

  useEffect(() => {
    if (!validationMessage && !mutation.isError && !dropMutation.isError) {
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      const invalid = sectionRef.current?.querySelector<HTMLElement>(
        '[aria-invalid="true"]'
      );
      invalid?.scrollIntoView?.({ block: 'center' });
      invalid?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [
    advancementErrorSignature,
    dropMutation.isError,
    mutation.isError,
    validationMessage,
  ]);

  const clearOutcome = () => {
    setValidationMessage(null);
    // The previous outcome no longer describes what is on screen.
    if (mutation.isSuccess || mutation.isError) mutation.reset();
    if (dropMutation.isSuccess || dropMutation.isError) dropMutation.reset();
  };

  return (
    <Box ref={sectionRef} component="section" width="100%">
      <Stack spacing={2}>
        {/* The pending request is standing state, not the outcome of the
              click just made: it steps aside while a fresh confirmation is on
              screen and comes back with the next render. */}
        {pendingDrop && !dropMutation.isSuccess && (
          <Alert severity="info">
            {describeManagedLeadPendingDrop(pendingDrop)}
          </Alert>
        )}

        {outstandingOutcome ? (
          <Alert
            severity="warning"
            role="status"
            action={
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                {outstandingOutcome.retryable && (
                  <Button
                    color="inherit"
                    onClick={retry}
                    disabled={mutation.isPending}
                  >
                    {mutation.isPending
                      ? 'Creazione Nota…'
                      : 'Riprova creazione Nota'}
                  </Button>
                )}
                <Button
                  color="inherit"
                  onClick={onRequestClose}
                  disabled={mutation.isPending}
                >
                  Chiudi
                </Button>
              </Stack>
            }
          >
            {outstandingOutcome.message}
          </Alert>
        ) : readOnly ? (
          <Typography color="text.secondary">
            La richiesta di Drop è in attesa di revisione. La lead è disponibile
            in sola lettura.
          </Typography>
        ) : !canUpdate && !dropOnly ? (
          <Typography color="text.secondary">
            Questa lead non è aggiornabile nella fase corrente.
          </Typography>
        ) : (
          <Box
            component="form"
            noValidate
            aria-busy={pending}
            sx={
              pending || blockedByStaleDetail
                ? { pointerEvents: 'none', opacity: 0.72 }
                : undefined
            }
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
          >
            {dropOnly ? (
              <Typography color="text.secondary">
                {`Questa lead non accetta avanzamenti in ${detail.stage.label}, ma puoi richiederne il Drop.`}
              </Typography>
            ) : (
              <SelectInput
                label="Stato pratica"
                disabled={pending || blockedByStaleDetail}
                value={dropSelected ? DROP_TARGET_VALUE : target?.id ?? ''}
                onChange={(event) => {
                  // Another destination is another form: a Nota written for the
                  // stage just left, and a Drop reason chosen for it, have no
                  // meaning here and must not be submitted by accident.
                  resetForm(event.target.value);
                  clearOutcome();
                }}
                error={validationMessage === STAGE_REQUIRED_MESSAGE}
                helperText={stageHelperText}
                selectOptions={[
                  ...transitions.map((transition) => ({
                    value: transition.id,
                    label: transition.label,
                  })),
                  // Drop is a request, not a transition: the current stage
                  // decides whether it is on offer. A pending request makes
                  // the whole lead read-only, so this menu is no longer shown.
                  ...(dropAllowed
                    ? [
                        {
                          value: DROP_TARGET_VALUE,
                          label: 'Drop',
                        },
                      ]
                    : []),
                ]}
              />
            )}

            {dropSelected && (
              <SelectInput
                label="Motivazione"
                disabled={pending || blockedByStaleDetail}
                value={dropReason}
                onChange={(event) => {
                  setDropReason(event.target.value);
                  clearOutcome();
                }}
                error={Boolean(reasonErrorMessage)}
                helperText={reasonErrorMessage}
                selectOptions={[
                  { value: '', label: 'Seleziona una motivazione' },
                  // The five approved reasons come from the contract: the
                  // backoffice keeps no copy of the list.
                  ...detail.dropReasons.map((reason) => ({
                    value: reason.key,
                    label: reason.label,
                  })),
                ]}
              />
            )}

            {unsupportedAdvancement ? (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {ADVANCEMENT_UNSUPPORTED_MESSAGE}
              </Alert>
            ) : (
              <ManagedLeadAdvancementFields
                fields={advancementFields}
                values={advancementValues}
                errors={advancementErrorMessages}
                disabled={pending || blockedByStaleDetail}
                onChangeValue={(key, value) => {
                  setAdvancementValues((current) => ({
                    ...current,
                    [key]: value,
                  }));
                  setFieldErrors(({ [key]: _corrected, ...rest }) => rest);
                  clearOutcome();
                }}
              />
            )}

            {noteRequired && (
              <TextInput
                type="text"
                label="Note"
                disabled={pending || blockedByStaleDetail}
                multiline
                minRows={3}
                value={note}
                onChange={(
                  event: React.ChangeEvent<
                    HTMLInputElement | HTMLTextAreaElement
                  >
                ) => {
                  setNote(event.target.value);
                  clearOutcome();
                }}
                error={Boolean(noteErrorMessage)}
                helperText={noteErrorMessage}
              />
            )}

            <Box
              data-modal-footer
              display="flex"
              justifyContent="center"
              gap={2}
              mt={4}
            >
              <Button type="button" onClick={onRequestClose} disabled={pending}>
                Annulla
              </Button>
              <CtaButton
                type="submit"
                variant="contained"
                disabled={unsupportedAdvancement || blockedByStaleDetail}
                loading={pending}
              >
                {pending ? 'Salvataggio…' : 'SALVA'}
              </CtaButton>
            </Box>
          </Box>
        )}

        {/* Outside the form: an advancement can leave the lead on a stage
              that accepts no further update, and the outcome of the update
              that took it there — a Nota still to retry, above all — must not
              disappear with the fields. */}
        {failure && (
          <ManagedLeadFailureAlert
            {...failure}
            severity="error"
            onRetry={retry}
          />
        )}

        {dropFailure && (
          <ManagedLeadFailureAlert {...dropFailure} onRetry={retryDrop} />
        )}
      </Stack>
    </Box>
  );
};

export default ManagedLeadUpdateSection;
